import { Check, Clock, Lock, ChevronRight } from 'lucide-react'

/**
 * StagePager — the 4D spine. v242.255.0 (s99): A LIST, NOT A PAGER.
 *
 * Dr. Rami 2026-10-10 ("Orella Case Steps" mockup, approved): "a list view of the steps that are
 * in sequence instead of the carousel". All four steps on screen at once, top to bottom, joined
 * by a rail that fills in each step's own colour as it completes. The 4-segment bar, the dots and
 * "scroll back for earlier steps" are gone — the rail is the progress indicator. The file name and
 * the default export stay, so every importer and gate that names StagePager still reaches it.
 *
 * ★ PRESENTATIONAL, AND DELIBERATELY SO (unchanged since v242.69.0). It takes the SAME `sections`
 *   array CaseHome builds — every complete / available / locked / viewable / pending /
 *   needsReview / externalUpload flag is computed upstream. If a row shows the wrong state, the
 *   bug is in `sections`, not here.
 * ★ A D's COLOUR IS ITS IDENTITY, NOT ITS STATE (PROGRESS_COLORS / BADGE_TEXT_COLORS). Reaching
 *   or completing a step changes strength, never hue.
 * ★ ONE FIXED LABEL PER STEP (labels.subtitles): Your Story · Examination · Doctor assessment ·
 *   Treatment & Follow-up. The state-dependent subtitles and the AI `blurb` line are no longer
 *   drawn (his call: "remove this AI gen row", "simplify each section").
 * ★ ONLY THE CURRENT STEP CARRIES A BUTTON, and it names the action (labels.actions). Every other
 *   row that can open is tappable as a whole, with its state on the right.
 * ★ MOTION IS THE APP'S OWN: the current step's dot uses `.breathe-ring` (ringBurstCard), the
 *   button `.breathe-navy`; both are off under prefers-reduced-motion in index.css.
 */

// v242.92.0 — activeStageIndex lives in ./stageModel.js so a gate can IMPORT and CALL it.
// ★★★ v242.185.0 — A RE-EXPORT BINDS NOTHING LOCALLY: the local import below is what the call
//   in StagePager() resolves to; the re-export is for the importers of this module.
import { activeStageIndex } from './stageModel'
export { activeStageIndex } from './stageModel'

const LOCKED_RAIL = '#E3EAF0'

function stateOf(section) {
  if (section.complete) return 'complete'
  if (section.available || section.needsReview || section.externalUpload) return 'active'
  if (section.pending) return 'waiting'
  if (section.viewable) return 'viewable'
  return 'locked'
}

export default function StagePager({
  sections = [],
  colors = {},
  textColors = {},
  activeIndex,
  onOpen,
  labels = {},
  buttonless = false,
}) {
  const idx = typeof activeIndex === 'number' ? activeIndex : activeStageIndex(sections)
  if (!sections.length) return null

  return (
    <div className="mb-3 bg-white rounded-[20px] border py-1.5" style={{ borderColor: '#E8EDF2' }}
         data-stage-list={sections.map(s => s.id).join('+')}>
      {sections.map((section, i) => (
        <StageCard
          key={section.id}
          index={i}
          first={i === 0}
          last={i === sections.length - 1}
          section={section}
          color={colors[section.id]}
          textColor={textColors[section.id]}
          live={i === idx}
          onOpen={onOpen}
          labels={labels}
          buttonless={buttonless}
        />
      ))}
    </div>
  )
}

export function StageCard({ index, first, last, section, color, textColor, live, onOpen, labels = {}, buttonless = false }) {
  const st = stateOf(section)
  const Icon = section.icon
  const hue = color || '#46A8F9'
  const canOpen = section.available || section.complete || section.viewable ||
                  section.pending || section.needsReview || section.externalUpload

  // ★★★ AN OBSERVATION IS NOT "IN PROGRESS" (kept from the pager; check-card-language
  //   [PAGER-BADGE] reads this order). The badge is the row's right-hand state.
  const isObservationStage = section.observation && section.available && !section.complete
  const badge = section.complete ? (labels.complete || 'Complete')
    : section.needsReview ? (labels.needsReview || 'Needs review')
    : section.externalUpload ? (labels.uploadNotes || 'Upload notes')
    : section.pending ? (section.consultPending ? (labels.pendingConsult || 'Doctor reviewing case') : (labels.pending || 'Pending review'))
    : isObservationStage ? (labels.observation || 'Observation')
    : section.available ? (labels.active || 'In progress')
    : section.viewable ? (labels.viewable || 'View')
    : (labels.locked || 'Locked')

  const actions = labels.actions || {}
  const action = section.externalUpload ? (labels.uploadNotes || 'Upload notes')
    : section.needsReview ? (actions.review || badge)
    : (actions[section.id] || labels.open || 'Open')
  // s124: Case Home's status card carries the live step's action (buttonless); the row stays tappable.
  const showButton = live && st === 'active' && !buttonless
  const rowTaps = canOpen && !showButton
  const subtitle = (labels.subtitles && labels.subtitles[section.id]) || section.subtitle

  const dotStyle = st === 'locked' ? { background: '#EEF3F7', color: '#9FB0BF' }
    : st === 'waiting' ? { background: '#fff', color: textColor || hue, border: `2px solid ${hue}` }
    : { background: hue, color: '#fff' }
  const DotIcon = section.complete ? Check : st === 'locked' ? Lock : st === 'waiting' ? Clock : (Icon || Clock)
  const railDone = st === 'complete'

  // s99 D477 (Build 61 TestFlight): a row whose tap does one known thing says it on the right
  //   ("View summary", "View / Add"). CaseHome sets section.trailLabel; the badge chain above
  //   (check-card-language [PAGER-BADGE]) is untouched.
  const trail = section.trailLabel && canOpen && !showButton ? section.trailLabel : null
  let right = null
  if (trail) {
    right = <span className="flex items-center gap-0.5" style={{ color: '#46A8F9' }} data-stage-trail={section.id}>{trail}<ChevronRight size={14} /></span>
  } else if (st === 'complete') {
    right = <span className="flex items-center gap-1" style={{ color: '#1B873A' }}><Check size={13} strokeWidth={2.6} />{badge}</span>
  } else if (st === 'locked') {
    right = <span className="flex items-center gap-1" style={{ color: '#A9B6C2' }}><Lock size={12} />{badge}</span>
  } else if (st === 'waiting') {
    right = <span className="flex items-center gap-1" style={{ color: '#8A6A1F' }}><Clock size={13} />{badge}</span>
  } else if (showButton) {
    right = isObservationStage
      ? <span className="rounded-full px-2.5 py-[3px]" style={{ background: '#E9F8FF', border: '1.5px solid #A7D9FC', color: '#15354F' }}>{badge}</span>
      : null
  } else if (canOpen) {
    right = <span className="flex items-center gap-0.5" style={{ color: '#46A8F9' }}>
      {st === 'viewable' ? (labels.view || 'View') : st === 'active' && !section.available ? badge : (labels.open || 'Open')}
      <ChevronRight size={14} />
    </span>
  }

  const Row = rowTaps ? 'button' : 'div'
  return (
    <Row
      type={rowTaps ? 'button' : undefined}
      onClick={rowTaps ? () => onOpen && onOpen(section) : undefined}
      data-stage-page={index}
      data-stage-id={section.id}
      data-stage-current={live ? '1' : undefined}
      className={`relative w-full flex items-start gap-3 text-left px-3.5 py-[11px] bg-transparent border-0${rowTaps ? ' cursor-pointer active:bg-[#F8FBFD]' : ''}`}
      style={{
        borderTop: first ? 'none' : '1px solid #F1F5F8',
        background: showButton ? 'linear-gradient(180deg,#FAFDFF,#F2F9FE)' : undefined,
      }}
    >
      {/* the rail: this step's own colour once it is complete */}
      <span aria-hidden="true" className="absolute"
            style={{ left: 28, width: 2, top: first ? '50%' : 0, bottom: last ? '50%' : 0,
                     background: railDone ? hue : LOCKED_RAIL }} />
      <span
        className={`relative rounded-full flex items-center justify-center flex-shrink-0${live && st !== 'locked' ? ' breathe-ring' : ''}`}
        style={{ width: 30, height: 30, marginTop: 1, zIndex: 1, ...dotStyle,
                 ...(live ? { '--ring-color': `${hue}8C`, '--ring-fade': `${hue}00`, '--ring-spread': '6px' } : {}) }}
      >
        <DotIcon size={st === 'locked' ? 12 : 14} strokeWidth={2.3} />
      </span>
      <span className="flex-1 min-w-0" style={{ paddingTop: 1 }}>
        {/* s126 (Dr. Rami 2026-10-31: "reverse the titles on the 4Ds — make the step name less prominent and what it's
            about more prominent"): the step name is the small label above (the header's area-label style), what the
            step is about is the line that reads first. */}
        <span className="block truncate font-bold uppercase" data-stage-step=""
              style={{ fontSize: 10.5, lineHeight: 1.2, letterSpacing: '0.09em', color: st === 'locked' ? '#B0BCC7' : '#46A8F9' }}>
          {section.title}
        </span>
        <span className="block font-extrabold truncate" data-stage-about=""
              style={{ fontSize: 15, lineHeight: 1.25, marginTop: 2, color: st === 'locked' ? '#9AA8B6' : '#15354F' }}>
          {subtitle}
        </span>
        {showButton && (
          <button
            type="button"
            onClick={() => onOpen && onOpen(section)}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-full cursor-pointer breathe-navy"
            style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, border: 'none', background: '#15354F', color: '#fff' }}
          >
            {action}
            <ChevronRight size={14} />
          </button>
        )}
      </span>
      {right && (
        <span className="flex-none self-center font-bold whitespace-nowrap" style={{ fontSize: 12 }}>{right}</span>
      )}
    </Row>
  )
}
